import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { forEach } from 'lodash';
import { useStoreState, useStoreActions } from 'stores/global';

export const useUrlState = <T>(
  id: string,
  initialState: T,
  persist: boolean = false,
  isOverwriteByInitialState: boolean = false,
  overwriteKeys?: string[]
): [T, (value: T | null) => void] => {
  const { persistedState } = useStoreState(state => state.urlState);
  const { updatePersistedState } = useStoreActions(state => state.urlState);

  const initialParams = persist
    ? formatState(
        initialState,
        persistedState?.[`${id}-urlState`] || {},
        isOverwriteByInitialState,
        overwriteKeys
      )
    : (initialState as Record<string, string>);

  const [searchParams, setSearchParams] = useSearchParams(
    Object.fromEntries(new URLSearchParams(initialParams))
  );

  useEffect(() => {
    if (initialState) {
      const defaults: Record<string, string> = {};
      forEach(initialState as Record<string, string>, (value, key) => {
        if (value != null) defaults[key] = String(value);
      });
      updatePersistedState({ [`${id}-urlDefaults`]: defaults });
    }
  }, [id]);

  useEffect(() => {
    if (persist && persistedState?.[`${id}-urlState`] != null) {
      setSearchParams(currentState => {
        const current = getEntries(currentState.entries());
        const formattedState = formatState(
          initialState,
          persistedState[`${id}-urlState`],
          isOverwriteByInitialState,
          overwriteKeys
        );
        const mergedState = { ...current, ...formattedState };
        updatePersistedState({ [`${id}-urlState`]: mergedState });
        return mergedState;
      });
    }
  }, [id]);

  const setUrlState = (state: T | null) => {
    setSearchParams(currentState => {
      const current = getEntries(currentState.entries());
      const newValues: Record<string, string> = {};
      const updatedPersistedState = { ...(persistedState?.[`${id}-urlState`] || {}) };

      if (state) {
        forEach(state, (value, key) => {
          const strValue = stringify(value);
          if (strValue != null && strValue !== '') {
            newValues[key] = strValue;
            if (persist) {
              updatedPersistedState[key] = strValue;
            }
          } else {
            delete current[key];
            if (persist) {
              delete updatedPersistedState[key];
            }
          }
        });

        if (persist) {
          updatePersistedState({ [`${id}-urlState`]: updatedPersistedState });
        }

        return { ...current, ...newValues };
      }

      return current;
    });
  };

  const urlState = getEntries(searchParams.entries(), true) as T;
  return [urlState, setUrlState];
};

// Merge logic: persistedState takes precedence, initial fills gaps
export const formatState = <T>(
  initialState: T,
  persistedState: Record<string, string>,
  isOverwriteByInitialState: boolean,
  overwriteKeys?: string[]
) => {
  const isOverwriteKeys = (overwriteKeys ?? [])?.length > 0;
  const state: Record<string, string> = {};
  // Add persistedState first
  if (persistedState) {
    forEach(persistedState, (value, key) => {
      if (value != null) {
        state[key] = value;
      }
    });
  }

  // Fill missing keys from initialState
  if (initialState) {
    forEach(initialState as Record<string, string>, (value, key) => {
      const shouldOverwrite =
        (value != null && state[key] == null) ||
        (isOverwriteByInitialState && (!isOverwriteKeys || overwriteKeys?.includes(key)));
      if (shouldOverwrite) {
        state[key] = value;
      }
    });
  }
  return state;
};

// Converts URLSearchParams entries into object, optionally parsing values
const getEntries = (entries: IterableIterator<[string, string]>, shouldParse: boolean = false) => {
  const object: Record<string, string> = {};
  for (const [key, value] of entries) {
    object[key] = shouldParse ? parse(value) : value;
  }
  return object;
};

// Serializes values to string for URL
const stringify = <T>(value: T): string | null => {
  if (value == null) return null;

  if (typeof value === 'string') return value;
  if (typeof value === 'number') return `n|${value}`;
  if (typeof value === 'boolean') return value ? '' : null;
  if (typeof value === 'object') return `o|${JSON.stringify(value)}`;

  return null;
};

// Deserializes string values from URL
export const parse = (value: string | null) => {
  if (value == null || value === 'null') return null;
  if (value.startsWith('n|')) return parseFloat(value.slice(2));
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value.startsWith('o|')) {
    try {
      return JSON.parse(value.slice(2));
    } catch {
      return null;
    }
  }
  return value;
};
